import FMP_CONFIG from './fmpConfig';

const configurationData = {
    supported_resolutions: ['1', '3', '5', '15', '30', '60', '120', '240', '360', '480', '720', '1D', '3D', '1W', '1M'],
    exchanges: [{
        value: 'FMP',
        name: 'Financial Modeling Prep',
        desc: 'FMP Enterprise'
    }],
    symbols_types: [{
        name: 'stock',
        value: 'stock'
    }, {
        name: 'commodity',
        value: 'commodity'
    }, {
        name: 'forex',
        value: 'forex'
    }]
};

const DEFAULT_BASE_URL = (FMP_CONFIG.baseUrl || 'https://financialmodelingprep.com/api/v3').replace(/\/$/, '');

// DXTrade WebSocket configuration
const DXTRADE_WS_URL = 'wss://alchemytvstage.prosp.devexperts.com/dxsca-web/md/?format=JSON';

// Suffix appended to symbols sent to/matched from DXTrade market data. Centralized here so it
// can be changed in one place if DXTrade's symbol format changes again — client currently wants
// plain symbols with no suffix at all.
const DXTRADE_SYMBOL_SUFFIX = '';

export class FMPDatafeed {
    constructor(options = {}) {
      this.apiKey = options.apiKey;
      this.subscribers = {};
      this.requestsPending = 0;
      this.historyLimit = options.limit || 1000;
      this.assetType = (options.type || 'STOCK').toUpperCase();
      this.symbolMetadata = new Map();
      this.lastBarsCache = new Map();
      this.historyCache = new Map(); // Cache to prevent duplicate requests
      
      // DXTrade user credentials (passed from chart page)
      this.dxUsername = options.dxUsername;
      this.dxClearingCode = options.dxClearingCode;
      this.dxSessionToken = options.dxSessionToken;

      // DXTrade WebSocket management
      this.dxWebSocket = null;
      this.dxReconnectAttempts = 0;
      this.dxMaxReconnectAttempts = 5;
      this.dxReconnectDelay = 5000;
      this.dxSubscriptions = new Map();
      this.dxWebSocketReady = false;
      this.dxPendingSubscriptions = [];
    }

    onReady(callback) {
      setTimeout(() => callback(configurationData));
    }

    async searchSymbols(userInput, exchange, symbolType, onResultReadyCallback) {
      console.log('searchSymbols', userInput, exchange, symbolType);
      try {
        const response = await fetch(`${DEFAULT_BASE_URL}/search?query=${userInput}&limit=10&apikey=${this.apiKey}`);
        const data = await response.json();
        
        const symbols = data.map(item => ({
          symbol: item.symbol,
          full_name: item.symbol,
          description: `${item.symbol} - ${item.name}`,
          exchange: item.exchangeShortName || item.exchange,
          type: (item.type || 'stock').toLowerCase(),
        }));
        
        onResultReadyCallback(symbols);
      } catch (error) {
        console.error('Error searching symbols:', error);
        onResultReadyCallback([]);
      }
    }

    async resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback) {
      try {
        const response = await fetch(`${DEFAULT_BASE_URL}/quote?symbol=${symbolName}&apikey=${this.apiKey}`);
        const data = await response.json();
        
        if (!data || data.length === 0) {
          throw new Error('Symbol not found');
        }
        
        let symboldata = data[0];
        
        let pricescale = 100;
        if (symboldata.price) {
          if (symboldata.price < 1) {
            pricescale = 10000;
          } else if (symboldata.price < 10) {
            pricescale = 1000;
          } else {
            pricescale = 100;
          }
        }
        console.log("symboldata : ", symboldata)
        const symbolInfo = {
            name: symboldata.symbol,
            description: symboldata.companyName || symboldata.symbol,
            type: this.assetType.toLowerCase(),
            session: this.assetType === 'STOCK' ? '0930-1600' : '24x7',
            timezone: this.assetType === 'STOCK' ? 'Etc/UTC' : 'Etc/UTC',
            exchange: symboldata.exchangeShortName || symboldata.exchange,
            minmov: 1,
            pricescale: pricescale,
            has_intraday: true,
            has_daily: true,
            has_weekly_and_monthly: true,
            supported_resolutions: configurationData.supported_resolutions,
            volume_precision: 0,
            data_status: 'streaming',
        };

        onSymbolResolvedCallback(symbolInfo);
      } catch (error) {
        console.error('Error resolving symbol:', error);
        onResolveErrorCallback('Error resolving symbol');
      }
    }
    
    async getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) {
      const { from, to, firstDataRequest } = periodParams;

      // Create cache key to prevent duplicate requests
      const cacheKey = `${symbolInfo.name}_${resolution}_${from}_${to}_${firstDataRequest}`;
      
      // Check if request is already in progress
      if (this.historyCache.has(cacheKey)) {
        const cachedData = this.historyCache.get(cacheKey);
        if (cachedData.pending) {
          // Wait for the pending request
          return cachedData.promise;
        }
        // Return cached result if available and recent (within 30 seconds)
        if (cachedData.timestamp && (Date.now() - cachedData.timestamp < 30000)) {
          onHistoryCallback(cachedData.bars, cachedData.meta);
          return;
        }
      }

      // Create promise for this request
      const requestPromise = (async () => {
        try {
          const interval = mapResolutionToFmp(resolution);
          if (!interval) {
            throw new Error(`Unsupported resolution: ${resolution}`);
          }

          const fromDate = toDateYMD(from);
          const toDate = toDateYMD(to);

          const params = new URLSearchParams({
            symbol: symbolInfo.name,
            apikey: this.apiKey,
          });

          if (!firstDataRequest && fromDate) {
            params.append('from', fromDate);
          }
          if (!firstDataRequest && toDate) {
            params.append('to', toDate);
          }

          let response;
          if (interval === '1day') {
            response = await fetch(`${DEFAULT_BASE_URL}/historical-price-eod/full?${params.toString()}`);
          } else {
            response = await fetch(`${DEFAULT_BASE_URL}/historical-chart/${interval}?${params.toString()}`);
          }
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          let data = await response.json();
          
          if (interval === '1day' && data.historical) {
            data = data.historical;
          }
          
          if (!Array.isArray(data) || data.length === 0) {
            const meta = { noData: true };
            this.historyCache.set(cacheKey, { bars: [], meta, timestamp: Date.now(), pending: false });
            onHistoryCallback([], meta);
            return;
          }
          
          const bars = data
            .map((source) => {
              return {
                time: parseTime(source.date),
                open: parseFloat(source.open),
                high: parseFloat(source.high),
                low: parseFloat(source.low),
                close: parseFloat(source.close),
                volume: parseFloat(source.volume ?? 0),
              };
            })
            .filter((bar) => {
              const isValid = Number.isFinite(bar.time);
              if (firstDataRequest) {
                return isValid;
              }
              const isInRange = bar.time >= from * 1000 && bar.time <= to * 1000;
              return isValid && isInRange;
            })
            .sort((a, b) => a.time - b.time);

          if (bars.length === 0) {
            const meta = { noData: true };
            this.historyCache.set(cacheKey, { bars: [], meta, timestamp: Date.now(), pending: false });
            onHistoryCallback([], meta);
            return;
          }

          const cacheKeySymbol = `${symbolInfo.name}_${resolution}`;
          this.lastBarsCache.set(cacheKeySymbol, bars[bars.length - 1]);

          const meta = {
            noData: false,
            nextTime: bars.length < 50 ? undefined : bars[0].time / 1000
          };
          
          // Cache the result
          this.historyCache.set(cacheKey, { bars, meta, timestamp: Date.now(), pending: false });
          onHistoryCallback(bars, meta);
          
        } catch (error) {
          console.error('[getBars]: Error:', error);
          this.historyCache.delete(cacheKey);
          onErrorCallback(error.message || 'Error getting bars');
        }
      })();

      // Mark as pending
      this.historyCache.set(cacheKey, { promise: requestPromise, pending: true });
      await requestPromise;
    }

    // Structured timestamped logger for all DXTrade events
    dxLog(level, tag, message, data) {
      const ts = new Date().toISOString();
      const prefix = `[DXTrade][${ts}][${tag}]`;
      if (data !== undefined) {
        console[level](prefix, message, data);
      } else {
        console[level](prefix, message);
      }
    }

    dxLogConnectionContext(tag, extra = {}) {
      this.dxLog('warn', tag, 'DXTrade connection context', {
        username: this.dxUsername,
        clearingCode: this.dxClearingCode,
        hasSessionToken: !!this.dxSessionToken,
        ...extra,
      });
    }

    dxLogRawFrame(direction, payload, extra = {}) {
      const normalizedPayload = typeof payload === 'string' ? payload : JSON.stringify(payload);
      this.dxLog('log', 'WS', `WebSocket ${direction} frame`, {
        direction,
        payloadLength: normalizedPayload ? normalizedPayload.length : 0,
        payload: normalizedPayload,
        ...extra,
      });
    }

    // Initialize DXTrade WebSocket connection
    async initializeDXWebSocket() {
      if (this.dxWebSocket && this.dxWebSocket.readyState === WebSocket.OPEN) {
        this.dxLog('log', 'INIT', 'WebSocket already connected — skipping. readyState=OPEN');
        return true;
      }

      if (this.dxWebSocket) {
        this.dxLog('warn', 'INIT', `WebSocket exists but not OPEN. readyState=${this.dxWebSocket.readyState}`);
      }

      if (!this.dxUsername || !this.dxClearingCode || !this.dxSessionToken) {
        this.dxLog('warn', 'INIT', 'Missing session info — cannot initialize.', {
          hasUsername: !!this.dxUsername,
          hasClearingCode: !!this.dxClearingCode,
          hasSessionToken: !!this.dxSessionToken,
        });
        this.dxLogConnectionContext('INIT', { url: DXTRADE_WS_URL, missingSessionInfo: true });
        return false;
      }

      this.dxLogConnectionContext('INIT', { url: DXTRADE_WS_URL, attemptingConnection: true });
      this.dxLog('log', 'INIT', 'DXTrade connection details', {
        url: DXTRADE_WS_URL,
        username: this.dxUsername,
        clearingCode: this.dxClearingCode,
        hasSessionToken: !!this.dxSessionToken,
      });

      return new Promise((resolve, reject) => {
        try {
          this.dxLog('log', 'INIT', `Connecting to WebSocket URL: ${DXTRADE_WS_URL}`);

          this.dxWebSocket = new WebSocket(DXTRADE_WS_URL);

          const connectionTimeout = setTimeout(() => {
            this.dxLog('error', 'INIT', 'Connection timeout after 10000ms. WebSocket did not open.');
            if (this.dxWebSocket) {
              this.dxWebSocket.close();
            }
            reject(new Error('WebSocket connection timeout'));
          }, 10000);

          this.dxWebSocket.onopen = async () => {
            clearTimeout(connectionTimeout);
            this.dxLog('log', 'CONN', `WebSocket opened. readyState=${this.dxWebSocket.readyState}. Attempting authentication...`);

            try {
              const sessionToken = await this.dxTradeLogin();

              if (!sessionToken) {
                this.dxLog('error', 'AUTH', 'dxTradeLogin returned null/empty session token.');
                throw new Error('Failed to authenticate with DXTrade');
              }

              this.dxWebSocketReady = true;
              this.dxReconnectAttempts = 0;

              this.dxLog('log', 'AUTH', 'Authentication successful. dxWebSocketReady=true.', {
                tokenLength: sessionToken.length,
                tokenPreview: `${sessionToken.slice(0, 6)}...${sessionToken.slice(-4)}`,
              });

              this.processPendingSubscriptions();

              resolve(true);
            } catch (error) {
              this.dxLog('error', 'AUTH', 'Authentication error:', error);
              if (this.dxWebSocket) {
                this.dxWebSocket.close();
              }
              reject(error);
            }
          };

          this.dxWebSocket.onmessage = (event) => {
            const rawMessage = typeof event.data === 'string'
              ? event.data
              : (event.data && typeof event.data.toString === 'function' ? event.data.toString() : String(event.data));

            this.dxLogRawFrame('received', rawMessage, {
              readyState: this.dxWebSocket ? this.dxWebSocket.readyState : null,
              messageType: typeof event.data,
            });
            this.handleDXMessage(rawMessage);
          };

          this.dxWebSocket.onerror = (error) => {
            this.dxLog('error', 'CONN', 'WebSocket error event fired.', error);
            clearTimeout(connectionTimeout);
          };

          this.dxWebSocket.onclose = (event) => {
            this.dxLog('warn', 'CONN', 'WebSocket closed.', {
              code: event.code,
              reason: event.reason || '(no reason)',
              wasClean: event.wasClean,
              dxWebSocketReadyWas: this.dxWebSocketReady,
            });
            this.dxWebSocketReady = false;

            if (!event.wasClean) {
              this.dxLog('warn', 'CONN', `Unclean close — scheduling reconnect. Attempts so far: ${this.dxReconnectAttempts}`);
              this.handleDXReconnect();
            }
          };

        } catch (error) {
          this.dxLog('error', 'INIT', 'Exception while initializing WebSocket:', error);
          reject(error);
        }
      });
    }

    async dxTradeLogin() {
      this.dxLog('log', 'AUTH', 'dxTradeLogin called.', {
        hasSessionToken: !!this.dxSessionToken,
        tokenLength: this.dxSessionToken ? this.dxSessionToken.length : 0,
      });
      this.dxLogConnectionContext('AUTH', { loginAttempt: true });
      return this.dxSessionToken;
    }

    handleDXReconnect() {
      if (this.dxReconnectAttempts >= this.dxMaxReconnectAttempts) {
        this.dxLog('error', 'RECONNECT', `Max reconnect attempts reached (${this.dxMaxReconnectAttempts}). Giving up.`);
        return;
      }

      this.dxReconnectAttempts++;
      const delay = this.dxReconnectDelay * this.dxReconnectAttempts;

      this.dxLog('warn', 'RECONNECT', `Scheduling reconnect in ${delay}ms.`, {
        attempt: this.dxReconnectAttempts,
        maxAttempts: this.dxMaxReconnectAttempts,
      });

      setTimeout(() => {
        this.dxLog('log', 'RECONNECT', `Reconnect attempt ${this.dxReconnectAttempts} firing now.`);
        this.initializeDXWebSocket().catch((error) => {
          this.dxLog('error', 'RECONNECT', 'Reconnection attempt failed:', error);
        });
      }, delay);
    }

    processPendingSubscriptions() {
      this.dxLog('log', 'SUB', `Processing pending subscriptions. Count: ${this.dxPendingSubscriptions.length}`);

      while (this.dxPendingSubscriptions.length > 0) {
        const subscription = this.dxPendingSubscriptions.shift();
        this.dxLog('log', 'SUB', `Flushing queued subscription: ${subscription.symbolInfo.name} ${subscription.resolution} uid=${subscription.subscriberUID}`);
        this.sendDXSubscription(
          subscription.symbolInfo,
          subscription.resolution,
          subscription.subscriberUID
        );
      }
    }

    sendDXSubscription(symbolInfo, resolution, subscriberUID) {
      if (!this.dxWebSocketReady || !this.dxWebSocket || this.dxWebSocket.readyState !== WebSocket.OPEN) {
        this.dxLog('warn', 'SUB', `WebSocket not ready — queuing subscription for ${symbolInfo.name} ${resolution}.`, {
          dxWebSocketReady: this.dxWebSocketReady,
          wsReadyState: this.dxWebSocket ? this.dxWebSocket.readyState : 'null',
          queueLengthAfter: this.dxPendingSubscriptions.length + 1,
        });
        this.dxPendingSubscriptions.push({ symbolInfo, resolution, subscriberUID });
        return;
      }

      const interval = mapResolutionToDXTrade(resolution);
      const requestId = `CANDLE_${symbolInfo.name}_${resolution}_${Date.now()}`;
      const fromTime = new Date().toISOString();

      const subscriptionRequest = {
          type: "MarketDataSubscriptionRequest",
          requestId: requestId,
          session: this.dxSessionToken,
          payload: {
              account: `${this.dxClearingCode}:${this.dxUsername}`,
              symbols: [ `${symbolInfo.name}${DXTRADE_SYMBOL_SUFFIX}` ],
              eventTypes: [
                  {
                      type: "Candle",
                      candleType: interval,
                      format: "COMPACT",
                      fromTime: fromTime,
                  }
              ]
          }
      };

      this.dxLog('log', 'SUB', `Actual market data subscription request for ${symbolInfo.name}`, {
        requestId,
        account: `${this.dxClearingCode}:${this.dxUsername}`,
        symbol: `${symbolInfo.name}${DXTRADE_SYMBOL_SUFFIX}`,
        candleType: interval,
        fromTime,
      });

      this.dxLog('log', 'SUB', `Sending subscription request for ${symbolInfo.name} (${resolution} → ${interval}).`, {
        requestId,
        account: `${this.dxClearingCode}:${this.dxUsername}`,
        symbol: `${symbolInfo.name}${DXTRADE_SYMBOL_SUFFIX}`,
        candleType: interval,
        fromTime,
        wsReadyState: this.dxWebSocket.readyState,
        sessionTokenPreview: this.dxSessionToken ? `${this.dxSessionToken.slice(0, 6)}...${this.dxSessionToken.slice(-4)}` : 'MISSING',
      });

      try {
        const serializedPayload = JSON.stringify(subscriptionRequest);
        this.dxLogRawFrame('sent', serializedPayload, {
          requestId,
          account: `${this.dxClearingCode}:${this.dxUsername}`,
          symbol: `${symbolInfo.name}${DXTRADE_SYMBOL_SUFFIX}`,
          candleType: interval,
        });
        this.dxWebSocket.send(serializedPayload);
        this.dxLog('log', 'SUB', `Subscription payload sent successfully for ${symbolInfo.name}.`);

        this.dxSubscriptions.set(subscriberUID, {
          symbolInfo,
          resolution,
          requestId,
          symbol: `${symbolInfo.name}${DXTRADE_SYMBOL_SUFFIX}`,
          period: interval,
        });

        this.dxLog('log', 'SUB', `Active dxSubscriptions count: ${this.dxSubscriptions.size}`);

      } catch (error) {
        this.dxLog('error', 'SUB', `Exception while sending subscription for ${symbolInfo.name}:`, error);
      }
    }

    handleDXMessage(message) {
      try {
        const response = JSON.parse(message);
        const responseType = response?.type || 'UNKNOWN';

        this.dxLog('log', 'MSG', `Incoming message type="${responseType}". Raw size=${message.length} bytes.`, response);

        if (responseType === 'Ping' || responseType === 'Heartbeat' || responseType === 'KeepAlive' || responseType === 'PingPong') {
          this.dxLog('warn', 'MSG', 'Heartbeat/ping message received from DXTrade.', response);
        }

        if (responseType === 'MarketDataSubscriptionResponse') {
          const status = response.payload?.status || 'UNKNOWN';
          const refRequestId = response.refRequestId || response.requestId || 'N/A';
          this.dxLog('log', 'MSG', `Subscription response: status=${status}, refRequestId=${refRequestId}.`, response);

          if (status !== 'SUBSCRIBED') {
            this.dxLog('warn', 'MSG', `Subscription NOT confirmed. Status="${status}". Full response:`, response);
          } else {
            this.dxLog('log', 'MSG', 'Successfully subscribed to market data stream.');
          }
        }
        else if (response.type === 'MarketData') {
          const eventCount = response.payload?.events?.length ?? 0;
          this.dxLog('log', 'MSG', `MarketData message received. Event count: ${eventCount}.`);
          this.handleMarketDataUpdate(response);
        }
        else if (response.type === 'Error') {
          this.dxLog('error', 'MSG', 'Server-side error response received:', response);
        }
        else {
          this.dxLog('log', 'MSG', `Unhandled message type="${responseType}". Full message:`, response);
        }

      } catch (error) {
        this.dxLog('error', 'MSG', 'Failed to parse incoming message:', { error, rawMessage: message });
      }
    }

    handleMarketDataUpdate(response) {
      try {
        if (!response.payload || !response.payload.events) {
          this.dxLog('warn', 'DATA', 'MarketData message has no payload.events. Full response:', response);
          return;
        }

        const updates = response.payload.events;
        this.dxLog('log', 'DATA', `Processing ${updates.length} event(s).`);

        for (const update of updates) {
          this.dxLog('log', 'DATA', `Event: type="${update.type}" symbol="${update.symbol}" time="${update.time}" open=${update.open} high=${update.high} low=${update.low} close=${update.close} volume=${update.volume}`);

          if (update.type !== 'Candle') {
            this.dxLog('log', 'DATA', `Skipping non-Candle event type="${update.type}".`);
            continue;
          }

          if (update.open === null || update.open === undefined) {
            this.dxLog('warn', 'DATA', `Candle for ${update.symbol} has null/undefined open — skipping bar construction.`, update);
            continue;
          }

          const activeSubscriptions = [...this.dxSubscriptions.entries()];
          this.dxLog('log', 'DATA', `Checking candle symbol="${update.symbol}" against ${activeSubscriptions.length} active subscription(s).`);

          let matchFound = false;

          for (const [subscriberUID, subscription] of activeSubscriptions) {
            const expectedSymbol = subscription.symbol;
            const incomingSymbol = update.symbol.replace(DXTRADE_SYMBOL_SUFFIX, '');
            const symbolMatch = expectedSymbol === incomingSymbol;

            if (!symbolMatch) {
              this.dxLog('log', 'DATA', `No match: subscription.symbol="${expectedSymbol}" vs incoming="${incomingSymbol}" (uid=${subscriberUID}).`);
              continue;
            }

            matchFound = true;
            this.dxLog('log', 'DATA', `Symbol match found for uid=${subscriberUID} (${expectedSymbol}). Building bar...`);

            const bar = {
              time: parseTime(update.time),
              open: parseFloat(update.open),
              high: parseFloat(update.high),
              low: parseFloat(update.low),
              close: parseFloat(update.close),
              volume: parseFloat(update.volume || 0),
            };

            this.dxLog('log', 'DATA', `Bar constructed for ${expectedSymbol}:`, {
              ...bar,
              timeISO: new Date(bar.time).toISOString(),
            });

            const cacheKey = `${subscription.symbolInfo.name}_${subscription.resolution}`;
            this.lastBarsCache.set(cacheKey, bar);

            if (this.subscribers[subscriberUID]) {
              this.dxLog('log', 'DATA', `Dispatching bar to TradingView callback for uid=${subscriberUID} symbol=${expectedSymbol}.`);
              this.subscribers[subscriberUID].callback(bar);
            } else {
              this.dxLog('warn', 'DATA', `No TradingView subscriber found for uid=${subscriberUID} — bar not dispatched.`, {
                registeredSubscriberUIDs: Object.keys(this.subscribers),
              });
            }
          }

          if (!matchFound) {
            this.dxLog('warn', 'DATA', `No subscription matched incoming candle symbol="${update.symbol}".`, {
              incomingSymbolStripped: update.symbol.replace(DXTRADE_SYMBOL_SUFFIX, ''),
              activeSubscriptions: activeSubscriptions.map(([uid, s]) => ({ uid, symbol: s.symbol })),
            });
          }
        }

      } catch (error) {
        this.dxLog('error', 'DATA', 'Exception in handleMarketDataUpdate:', error);
      }
    }

    async subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) {
        this.dxLog('log', 'SUB', `subscribeBars called: symbol=${symbolInfo.name} resolution=${resolution} uid=${subscriberUID}.`, {
          dxWebSocketReady: this.dxWebSocketReady,
          hasSessionToken: !!this.dxSessionToken,
          currentSubscribersCount: Object.keys(this.subscribers).length,
          currentDxSubscriptionsCount: this.dxSubscriptions.size,
        });

        try {
          if (!this.dxWebSocketReady && this.dxSessionToken) {
            this.dxLog('log', 'SUB', 'WebSocket not ready but session token present — initializing WebSocket...');
            await this.initializeDXWebSocket();
          } else if (!this.dxSessionToken) {
            this.dxLog('warn', 'SUB', 'No session token available — real-time data will not work.');
          }

          this.subscribers[subscriberUID] = {
            symbolInfo,
            resolution,
            callback: onRealtimeCallback,
            onResetCacheNeeded: onResetCacheNeededCallback,
          };

          this.dxLog('log', 'SUB', `Registered TradingView subscriber uid=${subscriberUID}. Total subscribers: ${Object.keys(this.subscribers).length}.`);

          if (this.dxWebSocketReady) {
            this.sendDXSubscription(symbolInfo, resolution, subscriberUID);
          } else {
            this.dxLog('warn', 'SUB', `WebSocket still not ready after init attempt — subscription for ${symbolInfo.name} will be queued.`);
          }

        } catch (error) {
          this.dxLog('error', 'SUB', `Exception in subscribeBars for ${symbolInfo.name}:`, error);
        }
    }

    unsubscribeBars(subscriberUID) {
      this.dxLog('log', 'SUB', `unsubscribeBars called for uid=${subscriberUID}.`);
      
      const subscription = this.dxSubscriptions.get(subscriberUID);

      if (subscription) {
        this.dxLog('log', 'SUB', `Removing dxSubscription for uid=${subscriberUID} symbol=${subscription.symbol}.`);
        if (this.dxWebSocketReady && this.dxWebSocket && this.dxWebSocket.readyState === WebSocket.OPEN) {
          // Unsubscribe request intentionally skipped — DX team confirmed not required for clean close
        }
        this.dxSubscriptions.delete(subscriberUID);
        this.dxLog('log', 'SUB', `Active dxSubscriptions after removal: ${this.dxSubscriptions.size}.`);
      } else {
        this.dxLog('warn', 'SUB', `unsubscribeBars: no dxSubscription found for uid=${subscriberUID}.`);
      }

      if (this.subscribers[subscriberUID]) {
        if (this.subscribers[subscriberUID].timer) {
          clearInterval(this.subscribers[subscriberUID].timer);
        }
        delete this.subscribers[subscriberUID];
        this.dxLog('log', 'SUB', `TradingView subscriber removed for uid=${subscriberUID}. Remaining: ${Object.keys(this.subscribers).length}.`);
      }
    }

    destroy() {
      console.log('[FMPDatafeed]: Destroying datafeed');
      
      for (const subscriberUID of Object.keys(this.subscribers)) {
        this.unsubscribeBars(subscriberUID);
      }
      
      if (this.dxWebSocket) {
        this.dxWebSocket.close();
        this.dxWebSocket = null;
      }
      
      this.lastBarsCache.clear();
      this.historyCache.clear();
      this.dxSubscriptions.clear();
      this.subscribers = {};
    }
}

function mapResolutionToFmp(resolution) {
  switch (resolution) {
    case '1': return '1min';
    case '5': return '5min';
    case '15': return '15min';
    case '30': return '30min';
    case '60': return '1hour';
    case '1D': return '1day';
    default: return '1min';
  }
}

function mapResolutionToDXTrade(resolution) {
  switch (resolution) {
    case '1': return '1m';
    case '3': return '3m';
    case '5': return '5m';
    case '15': return '15m';
    case '30': return '30m';
    case '60': return '1h';
    case '120': return '2h';
    case '240': return '4h';
    case '360': return '6h';
    case '480': return '8h';
    case '720': return '12h';
    case '1D': return '1d';
    case '3D': return '3d';
    case '1W': return '1w';
    case '1M': return '1M';
    default: return '1m';
  }
}

// US Eastern DST rule (since 2007): starts 2nd Sunday of March, ends 1st Sunday of November.
const nthSundayUTC = (year, month, n) => {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const firstSunday = 1 + ((7 - first.getUTCDay()) % 7);
  return Date.UTC(year, month - 1, firstSunday + (n - 1) * 7);
};

// FMP historical dates are US/Eastern wall-clock time; offset flips between EST (-5) and EDT (-4).
const getNYUtcOffsetHours = (year, month, day) => {
  const dstStart = nthSundayUTC(year, 3, 2);
  const dstEnd = nthSundayUTC(year, 11, 1);
  const current = Date.UTC(year, month - 1, day);
  return (current >= dstStart && current < dstEnd) ? 4 : 5;
};

const parseTime = (value) => {
  if (!value) return NaN;

  if (typeof value === 'number') return value * 1000;

  if (value.includes('T')) {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : NaN;
  }

  const parts = value.split(/[- :]/);
  if (parts.length >= 6) {
    const [year, month, day, hour, minute, second] = parts.map(Number);
    const utcMillis = Date.UTC(year, month - 1, day, hour + getNYUtcOffsetHours(year, month, day), minute, second);
    return utcMillis;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return Date.UTC(y, m - 1, d, 0, 0, 0);
  }

  return NaN;
};

const toDateYMD = (ts) => {
  if (ts === undefined || ts === null) {
    return undefined;
  }
  const date = new Date(ts * 1000);
  if (!Number.isFinite(date.getTime())) {
    return undefined;
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};